import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "wouter";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Field } from "../components/Field";
import { Footer } from "../components/Footer";
import { ApiError, apiSend } from "../lib/customFetch";
import styles from "./AuthLayout.module.css";

interface ResetRequestResponse {
  besked: string;
  mailStatus: "skjult";
}

function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div className={styles.page}>
      <div className={styles.box}>
        <div className={styles.brand}>
          app<span>smeden</span>
        </div>
        {children}
        <Footer />
      </div>
    </div>
  );
}

export function PasswordResetRequestPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    apiSend<ResetRequestResponse>("POST", "/api/password-reset", { email })
      .then(() => setSent(true))
      .catch((caught: unknown) => {
        setBusy(false);
        setError(
          caught instanceof ApiError && caught.status === 429
            ? "For mange forsøg — vent lidt og prøv igen."
            : "Anmodningen kunne ikke gennemføres — prøv igen.",
        );
      });
  };

  return (
    <AuthFrame>
      <Card title="Glemt adgangskode">
        {sent ? (
          <div className={styles.form}>
            <p className={styles.betaNote} role="status">
              Hvis emailadressen er registreret, har vi sendt et link til at
              vælge en ny adgangskode.
            </p>
            <p className={styles.betaNote}>
              Af sikkerhedshensyn viser vi ikke, om adressen findes eller om en
              konkret mail blev leveret. Tjek også spam-mappen.
            </p>
            <Link href="/login">Tilbage til login</Link>
          </div>
        ) : (
          <form className={styles.form} onSubmit={submit}>
            <p className={styles.betaNote}>
              Indtast din admin-email. Hvis den findes, sender vi et
              engangslink, som er gyldigt i én time.
            </p>
            <Field
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            {error !== null && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy}>
              {busy ? "Sender…" : "Send reset-link"}
            </Button>
            <Link href="/login">Tilbage til login</Link>
          </form>
        )}
      </Card>
    </AuthFrame>
  );
}

export function PasswordResetConfirmPage({ token }: { token: string }) {
  const [password, setPassword] = useState("");
  const [gentag, setGentag] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < 10) {
      setError("Adgangskoden skal være mindst 10 tegn.");
      return;
    }
    if (password !== gentag) {
      setError("De to adgangskoder er ikke ens.");
      return;
    }
    setBusy(true);
    apiSend<void>("POST", "/api/password-reset/confirm", {
      token,
      nytPassword: password,
    })
      .then(() => setDone(true))
      .catch((caught: unknown) => {
        setBusy(false);
        setError(
          caught instanceof ApiError && caught.status === 429
            ? "For mange forsøg — vent lidt og prøv igen."
            : "Linket er ugyldigt eller udløbet. Bed om et nyt reset-link.",
        );
      });
  };

  return (
    <AuthFrame>
      <Card title={done ? "Adgangskoden er ændret" : "Vælg ny adgangskode"}>
        {done ? (
          <div className={styles.form}>
            <p className={styles.betaNote} role="status">
              Du er logget ind med din nye adgangskode.
            </p>
            <Link href="/">Åbn AppSmeden</Link>
          </div>
        ) : (
          <form className={styles.form} onSubmit={submit}>
            <Field
              label="Ny adgangskode"
              type="password"
              autoComplete="new-password"
              hint="Mindst 10 tegn."
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <Field
              label="Gentag ny adgangskode"
              type="password"
              autoComplete="new-password"
              value={gentag}
              onChange={(event) => setGentag(event.target.value)}
              required
            />
            {error !== null && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy}>
              {busy ? "Gemmer…" : "Gem ny adgangskode"}
            </Button>
          </form>
        )}
      </Card>
    </AuthFrame>
  );
}
